import React from "react";
import {
  Box,
  Dialog,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
  Icon,
  IconTile,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Kbd,
  KbdGroup,
  NavPane,
  NavPaneContent,
  NavPaneEmpty,
  NavPaneGroup,
  NavPaneHeader,
  NavPaneItem,
  NavPaneTitle,
  ROW_RUNGS,
  ScrollArea,
  Separator,
  Text,
  useIsMobile,
} from "@salestrak/ui";

import { ManageDialogHeader } from "#app/components/manage-dialog-header";
import { useViewerIsShareOnly } from "#app/lib/hooks/use-permission";
import { useAutomationsEnabled } from "#app/lib/hooks/use-automations-enabled";
import { useAutomationPermissions } from "#app/hooks/automations/use-automation-permissions";
import {
  DEFAULT_SHORTCUT_SECTION,
  KEYBOARD_SHORTCUTS_SHORTCUT,
  searchShortcutSections,
  shortcutSectionsForViewer,
  shortcutEntryKey,
  type ShortcutEntry,
  type ShortcutGroup,
  type ShortcutSection,
} from "./keyboard-shortcuts-catalog";
import { SearchFieldHotkeyTooltip } from "./search-field-hotkey-tooltip";
import {
  useSlashSearchTarget,
  VIEW_SEARCH_SHORTCUT,
} from "#app/lib/hooks/use-slash-search";
import { useTransientValue } from "#app/lib/hooks/use-transient-value";
import { AnalyticsContext, NavigationEvent, useTrackSearch } from "#app/lib/analytics";

const DIALOG_TITLE = "Keyboard Shortcuts";
/** The reading column for a section: title, description and rows. Rows put
 *  the label at the left edge and the keycaps at the right, so the column is
 *  capped where a glance still connects the two — a full-pane row on a wide
 *  monitor strands the keycaps a metre from what they do. */
const CONTENT_MAX_WIDTH = 880;
/** One shortcut row's rung — taller than a nav row, shorter than a settings
 *  row: a scanning list, not a list of controls. */
const ROW_HEIGHT = ROW_RUNGS.tableRow;
/** How long a jumped-to row wears the selected wash before it fades. Long
 *  enough to find after the scroll settles, short enough to read as a flash
 *  rather than a selection. */
const HIGHLIGHT_MS = 1600;

/**
 * One chord as keycaps. A sequence ("G then T") joins its keys with the word;
 * a simultaneous chord ("⌘ K") sits them side by side in a `KbdGroup`. The
 * conjunction is muted running text, not a keycap — it is not a key.
 */
function Chord({
  keys,
  sequence,
}: {
  keys: readonly string[];
  sequence: boolean;
}) {
  if (!sequence) {
    return (
      <KbdGroup>
        {keys.map((key, index) => (
          <Kbd key={`${key}-${index}`}>{key}</Kbd>
        ))}
      </KbdGroup>
    );
  }
  return (
    <Box align="center" direction="row" gap="xs">
      {keys.map((key, index) => (
        <React.Fragment key={`${key}-${index}`}>
          {index > 0 ? (
            <Text size="xs" variant="muted">
              then
            </Text>
          ) : null}
          <Kbd>{key}</Kbd>
        </React.Fragment>
      ))}
    </Box>
  );
}

/** One shortcut row: what it does on the left, how to press it on the right.
 *  Alternatives ("N or P") read as running text between chords. The row
 *  washes on hover purely as a scanning aid — nothing here is pressable, so
 *  there is no cursor change and no press state. The wash bleeds one gutter
 *  step past the reading edges (negative margin + matching padding), so the
 *  text stays on the column's edge while the fill gets breathing room.
 *  A row jumped to from the sidemenu search scrolls into view wearing the
 *  selected wash for a moment, then fades back on the transition. */
function ShortcutRow({
  entry,
  highlighted = false,
}: {
  entry: ShortcutEntry;
  highlighted?: boolean;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (highlighted) {
      ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [highlighted]);
  return (
    <Box
      ref={ref}
      align="center"
      background={highlighted ? "selected" : undefined}
      className="transition-colors duration-500"
      direction="row"
      gap="md"
      hoverBackground
      justify="between"
      marginXNegative="sm"
      minHeight={ROW_HEIGHT}
      paddingX="sm"
      rounded="md"
    >
      <Box direction="column" gap="2xs" minWidth={0}>
        <Text size="sm">{entry.label}</Text>
        {entry.note ? (
          <Text size="xs" variant="muted">
            {entry.note}
          </Text>
        ) : null}
      </Box>
      <Box align="center" direction="row" gap="sm" shrink={false}>
        {entry.chords.map((keys, index) => (
          <React.Fragment key={index}>
            {index > 0 ? (
              <Text size="xs" variant="muted">
                or
              </Text>
            ) : null}
            <Chord keys={keys} sequence={entry.sequence ?? false} />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

/** One group of rows: an optional subheading (with its own one-liner) over a
 *  hairline, then the rows. An unlabeled group is just the hairline + rows —
 *  the section title above already introduced it. */
function GroupBlock({
  group,
  compact,
  sectionId,
  highlightKey,
}: {
  group: ShortcutGroup;
  compact: boolean;
  sectionId: string;
  highlightKey?: string | null | undefined;
}) {
  return (
    <Box direction="column" fill="width">
      {group.label ? (
        <Box direction="column" gap="2xs" paddingBottom="sm">
          <Text size={compact ? "base" : "lg"} weight="semibold">
            {group.label}
          </Text>
          {group.description ? (
            <Text size="sm" variant="muted">
              {group.description}
            </Text>
          ) : null}
        </Box>
      ) : null}
      <Separator />
      <Box direction="column" paddingTop="sm">
        {group.entries.map((entry, index) => (
          <ShortcutRow
            key={`${entry.label}-${index}`}
            entry={entry}
            highlighted={highlightKey === shortcutEntryKey(sectionId, entry)}
          />
        ))}
      </Box>
    </Box>
  );
}

/** A section's body: title, one-line scope description, then each group. The
 *  same block renders in the desktop pane and, once per section, stacked in
 *  the mobile drawer. */
function SectionBody({
  section,
  compact = false,
  highlightKey,
}: {
  section: ShortcutSection;
  /** The drawer's stacked rendering: smaller headings, since every section
   *  shares one scroll. */
  compact?: boolean;
  /** The entry a sidemenu search result jumped to, if any. */
  highlightKey?: string | null | undefined;
}) {
  return (
    <Box direction="column" fill="width" gap="lg">
      <Box direction="column" gap="2xs">
        <Text size={compact ? "lg" : "2xl"} weight="semibold">
          {section.label}
        </Text>
        <Text size="sm" variant="muted">
          {section.description}
        </Text>
      </Box>
      {section.groups.map((group, index) => (
        <GroupBlock
          key={`${group.label ?? "group"}-${index}`}
          compact={compact}
          group={group}
          highlightKey={highlightKey}
          sectionId={section.id}
        />
      ))}
    </Box>
  );
}

/**
 * The keyboard-shortcuts reference: every chord the app binds, grouped by
 * where it works, on the manage-dialog structure the App Center uses — a
 * `NavPane` of sections, a header naming the open one, and a scrolling
 * reading column. The catalog it renders is `shortcutSectionsForViewer`;
 * nothing here knows what a shortcut does.
 *
 * The sidemenu carries a search (focused on open — type-to-filter, the App
 * Center contract). While searching, the pane swaps to the sections that
 * contain the query, each with its matching shortcuts as jump rows beneath:
 * a section row opens the section; a shortcut row opens it AND scrolls to
 * that entry, flashing it with the selected wash. Clearing the query (the
 * ×, or Escape while the field has text) restores the plain section list.
 *
 * Phones get a drawer with every section stacked — a nav pane has no room
 * at that width, and the list is short enough to scroll.
 */
export function KeyboardShortcutsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const searchRef = React.useRef<HTMLInputElement>(null);
  // The desktop search is the `/` target for as long as the reference is
  // open (the mobile drawer has no search and no keyboard).
  const isSlashTarget = useSlashSearchTarget(searchRef, open && !isMobile);
  const [selectedId, setSelectedId] = React.useState(DEFAULT_SHORTCUT_SECTION.id);
  const [query, setQuery] = React.useState("");
  // The entry a search result jumped to; cleared after the flash fades.
  const [highlightKey, showHighlight, clearHighlight] = useTransientValue<string>(HIGHLIGHT_MS);
  // A fresh open starts at Global with no search, like the App Center starts
  // at Featured.
  React.useEffect(() => {
    if (!open) {
      setSelectedId(DEFAULT_SHORTCUT_SECTION.id);
      setQuery("");
      clearHighlight();
    }
  }, [open, clearHighlight]);

  const viewerIsShareOnly = useViewerIsShareOnly();
  // The same gate as every Automations door (useManageDialogDoors).
  const automationsEnabled = useAutomationsEnabled();
  const { canOpenManager } = useAutomationPermissions();
  const canOpenAutomations = automationsEnabled && canOpenManager;
  const sections = React.useMemo(
    () => shortcutSectionsForViewer(viewerIsShareOnly, canOpenAutomations),
    [viewerIsShareOnly, canOpenAutomations],
  );
  const active =
    sections.find((s) => s.id === selectedId) ?? DEFAULT_SHORTCUT_SECTION;
  const searching = query.trim().length > 0;
  const results = searching ? searchShortcutSections(query, sections) : [];
  useTrackSearch(query, () => ({
    context: AnalyticsContext.Navigation,
    event: NavigationEvent.SearchShortcuts,
    data: { resultCount: results.length },
  }));

  const jumpToEntry = (section: ShortcutSection, entry: ShortcutEntry) => {
    setSelectedId(section.id);
    showHighlight(shortcutEntryKey(section.id, entry));
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* The window tier, one step below the manage-dialog takeover: a
       * reference floats as a bounded window over the app instead of covering
       * it — reading it is a glance, not a work session. */}
      <DialogPopup
        initialFocus={searchRef}
        showCloseButton={false}
        size="window"
        // The manage-dialog room rests on the page fill: its nav pane lifts
        // above it and its tables read on the surface they read on everywhere.
        surface="background"
      >
        {/* Phone: the plain section list — no search, no keyboard. */}
        {isMobile ? (
          <>
            <DialogHeader>
              <DialogTitle>{DIALOG_TITLE}</DialogTitle>
            </DialogHeader>
            <DialogPanel>
              <Box direction="column" gap="xl" paddingBottom="md">
                {sections.map((section) => (
                  <SectionBody compact key={section.id} section={section} />
                ))}
              </Box>
            </DialogPanel>
          </>
        ) : (
          <>
            <DialogTitle visuallyHidden>{DIALOG_TITLE}</DialogTitle>
            {/* Bare `/` anywhere in the dialog returns focus to the search (the
              * key its tooltip advertises) — from a nav row, a jump row, or the
              * body a reader tabbed into. The search registers as the `/` target
              * while the reference is open (`useSlashSearchTarget` above), and a
              * dialog's own search outranks the page's behind it. */}
            <Box direction="row" fill minHeight={0}>
              <NavPane>
                <NavPaneHeader>
                  <IconTile bordered name="keyboard" size="lg" />
                  <NavPaneTitle>{DIALOG_TITLE}</NavPaneTitle>
                </NavPaneHeader>
                {/* The sidemenu search, on the pane gutter above the rows, on the
                    sidebar-header control rung (28px: `size="sm"`). 12px top
                    padding — the settings sidebar's search sits the same distance
                    under its header; the group's 8px rhythm reads crammed against
                    the header hairline. */}
                <Box paddingBottom="xs" paddingTop="base" paddingX="sm" shrink={false}>
                  <SearchFieldHotkeyTooltip
                    keys={isSlashTarget ? VIEW_SEARCH_SHORTCUT : undefined}
                    label="Search shortcuts"
                  >
                    <InputGroup>
                      <InputGroupAddon>
                        <Icon name="search" />
                      </InputGroupAddon>
                      <InputGroupInput
                        ref={searchRef}
                        aria-label="Search shortcuts"
                        placeholder="Search..."
                        size="sm"
                        type="search"
                        value={query}
                        onValueChange={setQuery}
                        onKeyDown={(e) => {
                          // Esc clears before it closes — the global-search ladder.
                          if (e.key === "Escape" && query.length > 0) {
                            e.stopPropagation();
                            setQuery("");
                          }
                        }}
                      />
                    </InputGroup>
                  </SearchFieldHotkeyTooltip>
                </Box>
                <NavPaneContent>
                  {searching ? (
                    results.length === 0 ? (
                      <NavPaneGroup>
                        <NavPaneEmpty>No shortcuts match</NavPaneEmpty>
                      </NavPaneGroup>
                    ) : (
                      results.map(({ section, entries }) => (
                        <NavPaneGroup key={section.id}>
                          <NavPaneItem
                            isActive={section.id === active.id}
                            onClick={() => setSelectedId(section.id)}
                          >
                            <Icon name={section.icon} />
                            {section.label}
                          </NavPaneItem>
                          {/* ps-9 lands the result label on the section rows'
                              label edge (8px pad + 16px glyph + 12px gap). */}
                          {entries.map((entry) => (
                            <NavPaneItem
                              key={shortcutEntryKey(section.id, entry)}
                              className="ps-9"
                              onClick={() => jumpToEntry(section, entry)}
                            >
                              <Text size="sm" truncate>
                                {entry.label}
                              </Text>
                            </NavPaneItem>
                          ))}
                        </NavPaneGroup>
                      ))
                    )
                  ) : (
                    <NavPaneGroup>
                      {sections.map((section) => (
                        <NavPaneItem
                          key={section.id}
                          isActive={section.id === active.id}
                          onClick={() => setSelectedId(section.id)}
                        >
                          <Icon name={section.icon} />
                          {section.label}
                        </NavPaneItem>
                      ))}
                    </NavPaneGroup>
                  )}
                </NavPaneContent>
                {/* The way back in, stated where the reader is already looking
                    for orientation. */}
                <Box
                  align="center"
                  direction="row"
                  gap="xs"
                  paddingBottom="md"
                  paddingX="md"
                  shrink={false}
                >
                  <KbdGroup>
                    {KEYBOARD_SHORTCUTS_SHORTCUT.map((key) => (
                      <Kbd key={key}>{key}</Kbd>
                    ))}
                  </KbdGroup>
                  <Text size="xs" variant="muted">
                    opens this from anywhere
                  </Text>
                </Box>
              </NavPane>

              <Box direction="column" fill minWidth={0}>
                {/* The header names WHERE you are — the open section — while the
                    dialog's identity lives in the pane header, as in the App
                    Center. */}
                <ManageDialogHeader onClose={onClose}>
                  <Box align="center" direction="row" gap="sm" minWidth={0}>
                    <Icon color="muted" name={active.icon} size="sm" />
                    <Text size="sm" truncate weight="medium">
                      {active.label}
                    </Text>
                  </Box>
                </ManageDialogHeader>
                <ScrollArea>
                  <Box
                    align="center"
                    direction="column"
                    fill="width"
                    paddingBottom="xl"
                    paddingTop="xl"
                    paddingX="lg"
                  >
                    <Box fill="width" maxWidth={CONTENT_MAX_WIDTH}>
                      <SectionBody highlightKey={highlightKey} section={active} />
                    </Box>
                  </Box>
                </ScrollArea>
              </Box>
            </Box>
      
          </>
        )}
      </DialogPopup>
    </Dialog>
  );
}
